import React from "react";
import { useFormikContext } from "formik";
import { Typography } from "@mui/material";
import FormField from "../../../utils/FormFileds";

const Step2 = ({ fields }) => {
  const { values, setFieldValue, setTouched, errors, touched, validateForm } =
    useFormikContext();

  // Function to mark all additional charges fields as touched
  const markAllAdditionalChargesAsTouched = () => {
    if (values.line_items && values.line_items.length > 0) {
      const touchedCharges = {};
      values.line_items.forEach((_, index) => {
        touchedCharges[index] = { amount: true, description: true };
      });

      setTouched({
        ...touched,
        line_items: touchedCharges,
      });
    }
  };

  // Function to validate step before proceeding
  const validateStep = async () => {
    // Mark all fields as touched first
    markAllAdditionalChargesAsTouched();

    // Trigger validation
    const formErrors = await validateForm();

    // Check if there are any errors in additionalCharges
    const hasAdditionalChargesErrors =
      formErrors.line_items &&
      Array.isArray(formErrors.line_items) &&
      formErrors.line_items.some(
        (charge) => charge && (charge.amount || charge.description)
      );

    return !hasAdditionalChargesErrors;
  };

  React.useEffect(() => {
    validateStep();
    // markAllAdditionalChargesAsTouched()
  }, [values]);

  const handleAddCharge = () => {
    const newCharges = [
      ...(values.line_items || []),
      { amount: "", description: "" },
    ];
    const newIndex = newCharges.length - 1;

    setFieldValue("line_items", newCharges);

    // Set the new fields as touched so validation errors show immediately
    setTouched({
      ...touched,
      line_items: {
        ...touched.line_items,
        [newIndex]: { amount: true, description: true },
      },
    });
  };

  // Remove an additional charge entry by index
  const handleRemoveCharge = (index) => {
    const newCharges = values.line_items.filter((_, i) => i !== index);
    setFieldValue("line_items", newCharges);

    // Clean up touched state for removed descriptions
    if (touched.line_items) {
      const newTouched = { ...touched.line_items };
      delete newTouched[index];

      // Reindex the remaining touched items
      const reindexedTouched = {};
      Object.keys(newTouched).forEach((key, idx) => {
        if (parseInt(key) > index) {
          reindexedTouched[idx] = newTouched[key];
        } else if (parseInt(key) < index) {
          reindexedTouched[key] = newTouched[key];
        }
      });

      setTouched({
        ...touched,
        line_items: reindexedTouched,
      });
    }
  };

  return (
    <div className="w-full ">
      <div className="flex flex-col gap-1 md:w-[900px] mt-1  ">
        <div className="bg-amber-50 border border-amber-300 rounded-lg px-3 py-1.5 mb-2 text-xs text-amber-800">
          Job cost only — do not include sales tax or processing fees. Properteasy calculates these separately.
        </div>
        <Typography
          variant="h2"
          sx={{ color: "#242424", lineHeight: "22px", textAlign: "left" }}
        >
          Materials & Labor Pricing
        </Typography>
        <div className="grid grid-cols-1 gap-2 w-full">
          {Object.values(fields)
            .slice(0, 2)
            .map((field) => (
              <FormField
                key={field.name}
                {...field}
                error={errors[field.name]}
                touched={touched[field.name]}
              />
            ))}
        </div>

        <Typography
          variant="h2"
          sx={{ color: "#242424", py: 0, textAlign: "center" }}
        >
          OR
        </Typography>

        <Typography
          variant="h2"
          sx={{ color: "#242424", lineHeight: "28px", textAlign: "left" }}
        >
          Flat Rate Pricing
        </Typography>
        <div className="grid grid-cols-1 gap-2 w-full">
          {Object.values(fields)
            .slice(2, 3)
            .map((field) => (
              <FormField
                key={field.name}
                {...field}
                error={errors[field.name]}
                touched={touched[field.name]}
              />
            ))}
        </div>

        <div>
          <Typography
            variant="h2"
            sx={{ color: "#242424", lineHeight: "22px", textAlign: "left" }}
          >
            Additional Charges
          </Typography>
          <div className="grid grid-cols-1 md:grid-cols-1 gap-2 w-full mt-1">
            {(values.line_items || []).map((charge, index) => (
              <div
                key={index}
                className="flex justify-center items-center gap-2"
              >
                <div className="grid grid-cols-2 gap-2 w-full">
                  <FormField
                    name={`line_items[${index}].amount`}
                    label="Additional Cost Incurred"
                    type="number"
                    placeholder="$500.00"
                    error={
                      errors.line_items?.[index]?.amount &&
                      touched.line_items?.[index]?.amount
                        ? errors.line_items[index].amount
                        : ""
                    }
                    touched={touched.line_items?.[index]?.amount}
                  />
                  <FormField
                    name={`line_items[${index}].description`}
                    label="Item"
                    type="text"
                    placeholder=""
                    error={
                      errors.line_items?.[index]?.description &&
                      touched.line_items?.[index]?.description
                        ? errors.line_items[index].description
                        : ""
                    }
                    touched={touched.line_items?.[index]?.description}
                  />
                </div>
                <button
                  onClick={() => handleRemoveCharge(index)}
                  className="text-red-400 border border-red-400 rounded-md px-3 mt-1 p-1 hover:bg-red-400 hover:text-white transition-colors duration-200"
                  aria-label="Remove charge"
                >
                  Remove
                </button>
              </div>
            ))}
          </div>
        </div>

        <div className="w-full flex justify-center">
          <button
            type="button"
            className="border-[#52C171] px-4 py-2 rounded-[12px] text-[#52C171] bg-[#52C1711A] hover:bg-transparent transition-all duration-300 ease-in-out"
            onClick={handleAddCharge}
          >
            Add Additional Charge
          </button>
        </div>
      </div>
    </div>
  );
};

// Export the validation function so it can be used by the parent component
export { Step2 as default };
